import React, { useCallback, useEffect, useRef, useState } from 'react';
import katex from 'katex';                 // ✅ V8 수식 렌더링
import 'katex/dist/katex.min.css';         // ✅ V8 수식 글꼴/스타일
import { questionAPI, adminAPI } from '../../api';
import './Exam.css';

interface Props {
  examType: '필기' | '실기';
  onBack: () => void;
  onModeChange?: (mode: 'normal' | 'mock' | 'wrong') => void;
}

// 과목별 색상 팔레트
const COLOR_PALETTE = [
  '#e74c3c', '#e67e22', '#f39c12', '#2ecc71',
  '#3498db', '#9b59b6', '#1abc9c', '#e91e63',
  '#00bcd4', '#ff5722',
];
const subjectColorMap: Record<string, string> = {};
let colorIdx = 0;
const getSubjectColor = (name: string) => {
  if (!name) return '#7f8c8d';
  if (!subjectColorMap[name]) {
    subjectColorMap[name] = COLOR_PALETTE[colorIdx % COLOR_PALETTE.length];
    colorIdx++;
  }
  return subjectColorMap[name];
};

// 드래그 리사이저 훅
const useResizer = (initialWidth: number, min: number, max: number, direction: 'right' | 'left' = 'right') => {
  const [width, setWidth] = useState(initialWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const adjusted = direction === 'right' ? delta : -delta;
      setWidth(w => Math.min(max, Math.max(min, startWidth.current + adjusted)));
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [min, max, direction]);

  return { width, onMouseDown };
};

const WrongNote: React.FC<Props> = ({ examType, onBack, onModeChange }) => {
  const [wrongs, setWrongs] = useState<any[]>([]);
  const [curIdx, setCurIdx] = useState(0);
  const [userAns, setUserAns] = useState<Record<number, any>>({});
  const [showAns, setShowAns] = useState<Record<number, boolean>>({});
  const [showExp, setShowExp] = useState<Record<number, boolean>>({});
  const [isMock, setIsMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoCanClose, setVideoCanClose] = useState(false);
  const [videoCountdown, setVideoCountdown] = useState(5);

  const openVideo = (url: string) => {
    if (!url?.trim()) return;
    setVideoUrl(url.trim());
    setVideoCanClose(false);
    setVideoCountdown(5);
    let count = 5;
    const timer = setInterval(() => {
      count--;
      setVideoCountdown(count);
      if (count <= 0) { clearInterval(timer); setVideoCanClose(true); }
    }, 1000);
  };
  const getYouTubeId = (url: string) => url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1] || null;

  // 휴식박스
  const [restBoxes, setRestBoxes] = useState<string[]>([]);
  const [displayBoxes, setDisplayBoxes] = useState<string[]>([]);
  const [restCount, setRestCount] = useState(8);
  const [restInterval, setRestInterval] = useState(300);
  const restScrollRef = useRef<HTMLDivElement>(null);
  const wrongListRef = useRef<HTMLDivElement>(null);
  const wrongLengthRef = useRef(0);
  const wrongIsFirstRender = useRef(true);

  // ✅ ← → 키 문제 이동
  useEffect(() => {
    wrongLengthRef.current = wrongs.length;
  }, [wrongs.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        if (tag === 'BUTTON') (e.target as HTMLElement).blur();
        if (e.key === 'ArrowRight') {
          setCurIdx(i => Math.min(wrongLengthRef.current - 1, i + 1));
        } else {
          setCurIdx(i => Math.max(0, i - 1));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  // ✅ 사이드바 중앙 고정
  useEffect(() => {
    if (wrongIsFirstRender.current) { wrongIsFirstRender.current = false; return; }
    if (!wrongListRef.current) return;
    const container = wrongListRef.current;
    const items = container.querySelectorAll('.q-list-row');
    const activeEl = items[curIdx] as HTMLElement;
    if (!activeEl) return;
    const containerH = container.clientHeight;
    const itemTop = activeEl.offsetTop - container.offsetTop;
    const itemH = activeEl.clientHeight;
    container.scrollTop = Math.max(0, itemTop - containerH / 2 + itemH / 2);
  }, [curIdx]);

  const sidebar = useResizer(220, 140, 340, 'right');
  const stats = useResizer(150, 100, 250, 'left');
  const rest = useResizer(220, 140, 360, 'left');

  // 오답 목록 로드
  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setWrongs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await questionAPI.wrongAnswers(examType, isMock);
      setWrongs(res.data);
    } catch {
      setWrongs([]);
    }
    setLoading(false);
  }, [examType, isMock]);

  useEffect(() => { load(); }, [load]);

  // 모드 전환 시 상태 초기화
  useEffect(() => {
    setCurIdx(0);
    setUserAns({});
    setShowAns({});
    setShowExp({});
  }, [isMock, examType]);

  // 휴식박스 로드
  useEffect(() => {
    adminAPI.restBoxes().then(res => {
      const boxes = res.data.map((b: any) => b.content).filter(Boolean);
      setRestBoxes(boxes);
      setDisplayBoxes([...boxes]);
    }).catch(() => {});
    adminAPI.settings().then(res => {
      setRestCount(res.data.rest_rotation_count ?? 8);
      setRestInterval(res.data.rest_normal_interval ?? 300);
    }).catch(() => {});
  }, []);

  // 휴식박스 자동 로테이션
  useEffect(() => {
    if (restBoxes.length < 2) return;
    const timer = setInterval(() => {
      setDisplayBoxes(prev => {
        const count = Math.min(restCount, prev.length - 1);
        return [...prev.slice(count), ...prev.slice(0, count)];
      });
    }, restInterval * 1000);
    return () => clearInterval(timer);
  }, [restBoxes, restCount, restInterval]);

  useEffect(() => {
    if (!restScrollRef.current) return;
    restScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [displayBoxes]);

  // [Nimg:경로] 파싱
  const parseImgTag = (content: string): { path: string; scale: number } | null => {
    const match = content.match(/^\[(\d+)img:(.+)\]$/);
    if (match) return { scale: Number(match[1]), path: match[2].trim() };
    return null;
  };

  // 문제/보기/해설 텍스트에서 이미지, <br>, _x000d_, [색상]텍스트[/] 파싱하여 렌더링
  const COLOR_MAP: Record<string, string> = {
    '빨강': '#e74c3c', '빨간': '#e74c3c', '빨간색': '#e74c3c',
    '파랑': '#3498db', '파란': '#3498db', '파란색': '#3498db',
    '초록': '#27ae60', '초록색': '#27ae60', '녹색': '#27ae60',
    '주황': '#e67e22', '주황색': '#e67e22', '오렌지': '#e67e22',
    '보라': '#9b59b6', '보라색': '#9b59b6',
    '노랑': '#f1c40f', '노란': '#f1c40f', '노란색': '#f1c40f',
    '분홍': '#e91e63', '분홍색': '#e91e63', '핑크': '#e91e63',
    '갈색': '#8b4513', '회색': '#7f8c8d', '그레이': '#7f8c8d',
    '검정': '#000000', '검정색': '#000000', '검은': '#000000',
    'red': '#e74c3c', 'blue': '#3498db', 'green': '#27ae60',
    'orange': '#e67e22', 'purple': '#9b59b6', 'yellow': '#f1c40f',
    'pink': '#e91e63', 'brown': '#8b4513', 'gray': '#7f8c8d',
    'grey': '#7f8c8d', 'black': '#000000',
  };

  // ✅ V8 수식 한 개 ($...$ → KaTeX)
  // 크기를 지정하지 않는다. KaTeX 가 em 단위로 그리므로 바깥 fontSize 를 그대로 따라간다.
  const renderMath = (tex: string, key: React.Key): React.ReactNode => {
    try {
      const html = katex.renderToString(tex, {
        throwOnError: false,   // 못 읽는 식은 붉게 표시만 되고 화면은 안 죽는다
        displayMode: false,    // 글줄 안에 흐르도록
        strict: false,
      });
      return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (e) {
      return <span key={key}>{'$' + tex + '$'}</span>;
    }
  };

  // ✅ V8 색상 구간처럼 이미 잘려 나온 조각 안에서 $수식$ 만 따로 처리
  const renderWithMath = (text: string, keyBase: string): React.ReactNode => {
    if (!text) return null;
    const bits = text.split(/(\$[^$\n]+\$)/g);
    return bits.map((b, j) =>
      /^\$[^$\n]+\$$/.test(b)
        ? renderMath(b.slice(1, -1), `${keyBase}-m${j}`)
        : <span key={`${keyBase}-t${j}`}>{b}</span>
    );
  };

  // $수식$ 도 함께 처리한다 (✅ V8)
  const renderText = (text: string): React.ReactNode => {
    if (!text) return null;
    let cleaned = text.replace(/_x000d_/gi, '').replace(/\r/g, '');
    // ✅ [색상]텍스트[/] → 색상 처리 (여는 태그는 COLOR_MAP에 등록된 색상명일 때만 인식)
    //    내부에 [kW],[A],[V] 등 단위 대괄호가 있어도 색칠 범위가 깨지지 않음
    const colorNames = Object.keys(COLOR_MAP)
      .sort((a, b) => b.length - a.length)
      .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const colorTagRe = new RegExp('\\[(' + colorNames.join('|') + ')\\]([\\s\\S]*?)\\[\\/\\]', 'gi');
    cleaned = cleaned.replace(colorTagRe, (match, color, content) => {
      const code = COLOR_MAP[color.toLowerCase()] || COLOR_MAP[color];
      if (code) return `@@COLOR:${code}@@${content}@@/COLOR@@`;
      return match;
    });
    // ✅ V8 맨 뒤에 $수식$ 추가. 앞의 네 가지는 손대지 않았다.
    const brPattern = new RegExp('(\\[\\d+:img:[^\\]]+\\]|\\[\\d+img:[^\\]]+\\]|<br\\s*\\/?>|@@COLOR:[^@]+@@[^@]*@@\\/COLOR@@|\\$[^$\\n]+\\$)', 'gi');
    const parts = cleaned.split(brPattern);
    return (
      <>
        {parts.map((part, i) => {
          const imgMatch = part.match(/^\[(\d+):img:(.+)\]$/) || part.match(/^\[(\d+)img:(.+)\]$/);
          if (imgMatch) {
            const scale = Number(imgMatch[1]);
            const path = imgMatch[2].trim();
            const src = path.startsWith('http') ? path : `/images/${path}`;
            return (
              <img key={i} src={src} alt="문제 이미지"
                style={{ width: `${scale}%`, maxWidth: '100%', borderRadius: 4, verticalAlign: 'middle' }}
                onError={e => (e.currentTarget.style.display = 'none')} />
            );
          }
          if (/^<br\s*\/?>$/i.test(part)) return <br key={i} />;
          // ✅ V8 수식
          if (/^\$[^$\n]+\$$/.test(part)) return renderMath(part.slice(1, -1), i);
          // 색상 태그 (✅ V8 색칠한 구간 안에 수식이 섞여 있어도 그려지도록)
          const colorMatch = part.match(/^@@COLOR:([^@]+)@@(.*)@@\/COLOR@@$/);
          if (colorMatch) return <span key={i} style={{ color: colorMatch[1] }}>{renderWithMath(colorMatch[2], `c${i}`)}</span>;
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  const renderRestContent = (content: string) => {
    if (!content) return null;
    const imgTag = parseImgTag(content);
    if (imgTag) {
      const src = imgTag.path.startsWith('http') ? imgTag.path : `/images/${imgTag.path}`;
      return (
        <div style={{ textAlign: 'center' }}>
          <img src={src} alt="휴식" style={{ width: `${imgTag.scale}%`, borderRadius: 6 }}
            onError={e => (e.currentTarget.style.display = 'none')} />
        </div>
      );
    }
    if (content.includes('youtube.com') || content.includes('youtu.be')) {
      const match = content.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      if (match?.[1]) {
        return (
          <iframe width="100%" height={Math.round(rest.width * 0.56)}
            src={`https://www.youtube.com/embed/${match[1]}`}
            frameBorder="0" allowFullScreen title="휴식 영상" />
        );
      }
    }
    if (content.startsWith('http') && /\.(png|jpg|gif|webp)/.test(content)) {
      return <img src={content} alt="휴식" style={{ width: '100%', borderRadius: 6 }}
        onError={e => (e.currentTarget.style.display = 'none')} />;
    }
    return <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{content}</div>;
  };

  // 정답 확인 (필기: 맞으면 자동 제거)
  const checkAnswer = async () => {
    if (!q) return;
    const ua = userAns[q.id];
    if (ua === undefined) { alert('답을 선택해주세요.'); return; }

    const isCorrect = String(ua) === String(q.answer);
    if (isCorrect) {
      alert('✅ 정답입니다! 오답노트에서 제거됩니다.');
      await doDelete(q, curIdx);
    } else {
      alert(`❌ 오답입니다.\n정답: ${q.answer}번`);
      setShowAns(prev => ({ ...prev, [q.id]: true }));
    }
  };

  // 삭제 (단건) - 현재 API: DELETE /api/questions/wrong/ { question_id, exam_type, is_mock }
  const doDelete = async (qa: any, idx: number) => {
    try {
      await questionAPI.deleteWrong({ question_id: qa.id, exam_type: examType, is_mock: isMock });
    } catch {}
    const next = wrongs.filter((_, i) => i !== idx);
    setWrongs(next);
    setUserAns({});
    setShowAns({});
    setShowExp({});
    setCurIdx(Math.min(idx, Math.max(0, next.length - 1)));
  };

  const deleteCurrentWrong = async () => {
    if (!window.confirm('현재 문제를 오답노트에서 삭제하시겠습니까?')) return;
    await doDelete(q, curIdx);
  };

  const deleteAllWrong = async () => {
    if (!window.confirm(`오답노트 전체 ${wrongs.length}문제를 삭제하시겠습니까?`)) return;
    // 전체 순차 삭제
    try {
      for (const wr of wrongs) {
        const qa = wr.question_data;
        if (qa) await questionAPI.deleteWrong({ question_id: qa.id, exam_type: examType, is_mock: isMock });
      }
    } catch {}
    setWrongs([]);
    setUserAns({});
    setShowAns({});
    setShowExp({});
    setCurIdx(0);
  };

  // 이전/다음 이동 시 해당 문제 상태 초기화
  const goTo = (idx: number) => {
    setCurIdx(idx);
    // 이동하는 문제의 답변 상태 초기화 (HTML 원본 동일 동작)
    if (wrongs[idx]) {
      const targetQ = wrongs[idx].question_data;
      if (targetQ) {
        setUserAns(prev => { const n = { ...prev }; delete n[targetQ.id]; return n; });
        setShowAns(prev => { const n = { ...prev }; delete n[targetQ.id]; return n; });
        setShowExp(prev => { const n = { ...prev }; delete n[targetQ.id]; return n; });
      }
    }
  };

  // 로딩
  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // 오답 없음
  if (wrongs.length === 0) return (
    <div className="question-layout">
      <div className="q-topbar">
        <div className="q-topbar-left">
          <button className="btn btn-sm q-topbar-back-btn" onClick={onBack}>← 이전</button>
          <div className="q-topbar-info">
            <span className="q-topbar-title">오답노트 [{examType}]</span>
          </div>
          <div className="q-topbar-modes">
            <button className="q-mode-btn" onClick={() => onModeChange?.('normal')}>일반</button>
            <button className="q-mode-btn" onClick={() => onModeChange?.('mock')}>모의고사</button>
            <button className="q-mode-btn active">오답노트</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
            <button className={`q-mode-btn ${!isMock ? 'active' : ''}`} onClick={() => setIsMock(false)}>일반오답</button>
            <button className={`q-mode-btn ${isMock ? 'active' : ''}`} onClick={() => setIsMock(true)}>모의오답</button>
          </div>
        </div>
      </div>
      <div className="empty-state" style={{ flex: 1, padding: 80 }}>
        <div className="icon">✅</div>
        <p>{isMock ? '모의고사' : '일반'} 오답노트가 비어있습니다!</p>
        <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={onBack}>← 종목선택</button>
      </div>
    </div>
  );

  const w = wrongs[curIdx];
  const q = w?.question_data;
  if (!q) return null;

  const subjectColor = getSubjectColor(q.subject_name || '');
  const isCorrect = userAns[q.id] !== undefined && String(userAns[q.id]) === String(q.answer);
  const answeredCount = wrongs.filter(wr => userAns[wr.question_data?.id] !== undefined).length;
  const correctCount = wrongs.filter(wr => {
    const qa = wr.question_data;
    return qa && userAns[qa.id] !== undefined && String(userAns[qa.id]) === String(qa.answer);
  }).length;

  return (
    <div className="question-layout">
      {/* 동영상 팝업 */}
      {videoUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', background: '#000', borderRadius: 12, padding: 10, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => videoCanClose && setVideoUrl(null)}
              style={{ position: 'absolute', top: -16, right: -16, width: 36, height: 36,
                background: videoCanClose ? '#e74c3c' : '#888',
                color: 'white', border: 'none', borderRadius: '50%', fontSize: videoCanClose ? 20 : 13,
                cursor: videoCanClose ? 'pointer' : 'not-allowed', zIndex: 1, fontWeight: 700 }}>
              {videoCanClose ? '✕' : videoCountdown}
            </button>
            {getYouTubeId(videoUrl) ? (
              <iframe width="800" height="450"
                src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}?autoplay=1`}
                frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen style={{ borderRadius: 6, maxWidth: '85vw', maxHeight: '80vh', display: 'block' }} title="동영상" />
            ) : videoUrl.match(/\.(mp4|webm|ogg)$/i) ? (
              <video controls autoPlay style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 6 }}>
                <source src={videoUrl} />
              </video>
            ) : (
              <div style={{ padding: 20, color: 'white', textAlign: 'center' }}>
                <a href={videoUrl} target="_blank" rel="noreferrer"
                  style={{ color: '#3498db', textDecoration: 'underline' }}>{videoUrl}</a>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 상단 topbar */}
      <div className="q-topbar">
        <div className="q-topbar-left">
          <button className="btn btn-sm q-topbar-back-btn" onClick={onBack}>← 이전</button>
          <div className="q-topbar-info">
            <span className="q-topbar-title">오답노트 [{examType}]</span>
            <span className="q-topbar-count">총 {wrongs.length}문제</span>
          </div>
          <div className="q-topbar-modes">
            <button className="q-mode-btn" onClick={() => onModeChange?.('normal')}>일반</button>
            <button className="q-mode-btn" onClick={() => onModeChange?.('mock')}>모의고사</button>
            <button className="q-mode-btn active">오답노트</button>
          </div>
          {/* 일반/모의 오답 전환 */}
          <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
            <button className={`q-mode-btn ${!isMock ? 'active' : ''}`} onClick={() => setIsMock(false)}>일반오답</button>
            <button className={`q-mode-btn ${isMock ? 'active' : ''}`} onClick={() => setIsMock(true)}>모의오답</button>
          </div>
        </div>
        <div className="q-topbar-right">
          <span className="q-topbar-user">{curIdx + 1} / {wrongs.length}</span>
        </div>
      </div>

      {/* 4등분 본문 */}
      <div className="question-body">

        {/* 1. 사이드바 - 오답 목록 */}
        <aside className="q-sidebar" style={{ width: sidebar.width }}>
          <div className="q-list-label" style={{ color: '#e74c3c', fontWeight: 700 }}>
            ❌ 오답 목록 {wrongs.length}문제
          </div>
          <div className="q-list-items" ref={wrongListRef}>
            {wrongs.map((wr, i) => {
              const qa = wr.question_data;
              if (!qa) return null;
              const ans = userAns[qa.id];
              const cor = ans !== undefined && String(ans) === String(qa.answer);
              const wrg = ans !== undefined && !cor;
              const subColor = getSubjectColor(qa.subject_name || '');
              const prevSubject = i > 0 ? (wrongs[i-1].question_data?.subject_name || '') : '';
              const curSubject = qa.subject_name || '';
              const isNewSubject = curSubject !== prevSubject;
              return (
                <React.Fragment key={wr.id}>
                  {isNewSubject && curSubject && (
                    <div className="q-subject-divider"
                      style={{ '--subject-color': subColor } as React.CSSProperties}>
                      {curSubject}
                    </div>
                  )}
                  <button
                    className={`q-list-row ${i === curIdx ? 'active' : ''} ${cor ? 'correct' : ''} ${wrg ? 'wrong' : ''}`}
                    style={{ '--subject-color': subColor } as React.CSSProperties}
                    onClick={() => goTo(i)}>
                    [{qa.year}-{qa.round}]&nbsp;
                    <span style={{ fontWeight: 700 }}>{qa.subject_name || ''}</span>
                    &nbsp;{qa.number}번
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </aside>

        <div className="q-resizer" onMouseDown={sidebar.onMouseDown} />

        {/* 2. 메인 문제 영역 */}
        <main className="q-main">
          {/* 문제 헤더 */}
          <div className="q-header">
            <div className="q-info">
              <span className={`q-type-badge ${examType === '실기' ? 'silgi' : ''}`}>{examType}</span>
              <span className="q-type-badge wrong-badge">오답노트</span>
              <span className="q-subject-badge" style={{
                background: subjectColor + '22',
                color: subjectColor,
              }}>{q.subject_name || ''}</span>
              <span className="q-meta">No.{q.number} | {q.year}년 | {q.round}차 | {q.q_type || '-'}</span>
              {q.video && (q.video.includes('http') || q.video.includes('youtu') || q.video.match(/\.(mp4|webm|ogg)$/i)) && (
                <span className="q-video-badge" title="동영상 보기"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openVideo(q.video)}>📹</span>
              )}
            </div>
            <div className="q-progress">{curIdx + 1} / {wrongs.length}</div>
          </div>

          {/* 문제 내용 */}
          <div className="q-content">
            <div className="q-text">{renderText(q.question)}</div>
            {q.image && (
              <div className="q-image">
                <img src={q.image} alt="문제 이미지"
                  onError={e => (e.currentTarget.style.display = 'none')} />
              </div>
            )}
          </div>

          {/* 선택지 (필기) */}
          {examType === '필기' && (
            <div className="q-options">
              {[1,2,3,4].map(n => {
                const optText = q[`option${n}`];
                if (!optText) return null;
                const selected = userAns[q.id] === n;
                const answered = showAns[q.id];
                const correct = Number(n) === Number(q.answer);
                return (
                  <button key={n}
                    className={`q-option ${selected ? 'selected' : ''} ${answered && correct ? 'correct' : ''} ${answered && selected && !correct ? 'wrong' : ''}`}
                    onClick={() => {
                      if (showAns[q.id]) return;
                      setUserAns(prev => ({ ...prev, [q.id]: n }));
                    }}>
                    <span className="option-num">{['①','②','③','④'][n-1]}</span>
                    <span className="option-text">{renderText(optText)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* 실기 답안 입력 */}
          {examType === '실기' && (
            <div className="silgi-answer">
              <textarea className="form-control" placeholder="답안을 입력하세요..." rows={4}
                value={userAns[q.id] || ''}
                onChange={e => setUserAns(prev => ({ ...prev, [q.id]: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </div>
          )}

          {/* 정답/해설 표시 */}
          {showAns[q.id] && (
            <div className="q-answer-section">
              <div className="q-answer">
                <span className="answer-label">✓ 정답</span>
                <span className="answer-value">
                  {examType === '필기' ? `${q.answer}번` : renderText(q.answer)}
                </span>
                {examType === '필기' && userAns[q.id] !== undefined && (
                  <span className={`answer-result ${isCorrect ? 'correct' : 'wrong'}`}>
                    {isCorrect ? '🎉 정답!' : '❌ 오답'}
                  </span>
                )}
              </div>
              {showExp[q.id] && q.explanation && (
                <div className="q-explanation">
                  <div className="exp-label">📝 해설</div>
                  <div className="exp-text">{renderText(q.explanation)}</div>
                </div>
              )}
            </div>
          )}

          {/* 버튼 */}
          <div className="q-buttons">
            <button className="btn btn-ghost"
              onClick={() => goTo(Math.max(0, curIdx - 1))}
              disabled={curIdx === 0}>◀ 이전</button>

            {examType === '필기' ? (
              <button className="btn btn-primary"
                onClick={checkAnswer}
                disabled={userAns[q.id] === undefined}>
                정답 확인
              </button>
            ) : (
              <button className="btn btn-primary"
                onClick={() => setShowAns(prev => ({ ...prev, [q.id]: !prev[q.id] }))}>
                {showAns[q.id] ? '정답 숨기기' : '정답 확인'}
              </button>
            )}

            <button className="btn btn-ghost"
              onClick={() => {
                setShowExp(prev => ({ ...prev, [q.id]: !prev[q.id] }));
                if (!showExp[q.id]) setShowAns(prev => ({ ...prev, [q.id]: true }));
              }}
              disabled={!showAns[q.id]}>해설보기</button>

            <button className="btn btn-ghost"
              onClick={() => goTo(Math.min(wrongs.length - 1, curIdx + 1))}
              disabled={curIdx === wrongs.length - 1}>다음 ▶</button>

            <button className="btn btn-danger" onClick={deleteCurrentWrong}>
              🗑️ 현재삭제
            </button>
          </div>
        </main>

        <div className="q-resizer" onMouseDown={stats.onMouseDown} />

        {/* 3. 현황판 + 오답노트 관리 */}
        <aside className="q-stats" style={{ width: stats.width }}>
          <div className="stats-title">📊 현황판</div>
          <div className="stats-row"><span>총 오답</span><strong style={{ color: '#e74c3c' }}>{wrongs.length}</strong></div>
          <div className="stats-row"><span>푼 문항</span><strong>{answeredCount}</strong></div>
          <div className="stats-row correct-row"><span>맞춤</span><strong>{correctCount}</strong></div>
          <div className="stats-row wrong-row">
            <span>틀림</span>
            <strong>{answeredCount - correctCount}</strong>
          </div>
          <div className="stats-divider" />
          <div className="stats-rate">
            {answeredCount > 0 ? Math.round(correctCount / answeredCount * 100) : 0}%
            <div className="stats-rate-label">정답률</div>
          </div>
          <div className="stats-divider" />
          {/* 오답노트 관리 버튼 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 2 }}>
              🗑️ 관리
            </div>
            <button className="btn btn-danger btn-sm"
              style={{ width: '100%', fontSize: 11 }}
              onClick={deleteCurrentWrong}>
              현재 문제 삭제
            </button>
            <button className="btn btn-sm"
              style={{ width: '100%', fontSize: 11, background: '#c0392b', color: 'white' }}
              onClick={deleteAllWrong}>
              전체 삭제
            </button>
          </div>
        </aside>

        <div className="q-resizer" onMouseDown={rest.onMouseDown} />

        {/* 4. 휴식박스 */}
        <aside className="q-rest" style={{ width: rest.width }}>
          <div className="rest-header">
            <span>🎨 휴식</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{displayBoxes.length}개</span>
          </div>
          <div className="rest-scroll" ref={restScrollRef}>
            {displayBoxes.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                휴식 콘텐츠가 없습니다
              </div>
            ) : (
              displayBoxes.map((box, i) => (
                <div key={i} className="rest-item">
                  {renderRestContent(box)}
                </div>
              ))
            )}
          </div>
        </aside>

      </div>
    </div>
  );
};

export default WrongNote;
